import { IStyleRule, IVectorStyle } from "../model/vector-style-definition.interface";
import { flattenSimpleFilter, parseLeafTriple } from "./vector-style-condition.util";

/** A single OpenLayers flat-style rule, matching `ol/style/flat`'s `Rule` shape. */
interface OlFlatRule {
  style: Record<string, unknown>;
  filter?: unknown[];
  else?: boolean;
}

/**
 * OpenLayers flat-style value accepted by `layer.setStyle(...)`: a plain properties object for a
 * uniform style, or an array of rules evaluated in order for a per-feature style. Property keys and
 * expression values (e.g. `["interpolate", ...]`, `["match", ...]`) are evaluated natively by
 * OpenLayers — this module does not interpret them itself.
 */
type OlFlatStyleLike = Record<string, unknown> | OlFlatRule[];

/**
 * Unwraps a wire-format {@link IVectorStyle} (`{ type, properties | rules }`) into the value
 * `layer.setStyle(...)` expects. Pure format translation: the style content is handed to OpenLayers
 * exactly as received — no validation, sanitizing or coercion. Returns null only when there is no
 * style or its `type` is unknown.
 */
export function convertVectorStyle(styleDef: IVectorStyle | undefined | null): OlFlatStyleLike | null {
  if (!styleDef) return null;
  if (styleDef.type === "flat") return styleDef.properties;
  if (styleDef.type === "rules") return styleDef.rules as OlFlatRule[];
  return null;
}

/**
 * Deterministically rebuilds the property/breakpoints legend metadata from an edited rules array —
 * no AI recompute needed, since a manual style edit only ever changes the rules themselves (the
 * thresholds ARE the breakpoints). `breakpoint_method` is always "manual" here since this only
 * runs after a hand-edit; dataset-derived fields (data_range, feature_count, description) are
 * intentionally not reconstructed — they describe the data, not the style, and are dropped rather
 * than guessed at from stale AI-generated text.
 *
 * Understands simple comparison filters (`[op, ["get", field], literal]`, either order) as well as a
 * single level of `all`/`any` compound filters (each leaf inspected independently via
 * {@link flattenSimpleFilter} + {@link parseLeafTriple} — the same canonical leaf parser the rule editor
 * and legend use, so this can't drift from what they understand); rules using the wider filter grammar
 * (match/case/in/between/nested compounds/...) are silently skipped here since this only feeds a UI hint,
 * not a correctness-critical path.
 */
export function deriveMetadataFromRules(rules: IStyleRule[]): {
  property_used?: string;
  breakpoints: number[];
  breakpoint_method: "manual";
} {
  const conditionRules = rules.filter((rule) => !rule.else);

  let property_used: string | undefined;
  const breakpoints = new Set<number>();

  conditionRules.forEach((rule) => {
    flattenSimpleFilter(rule.filter).forEach((part) => {
      const leaf = parseLeafTriple(part);
      if (!leaf) return;
      if (property_used === undefined) property_used = leaf.propertyPath.join(".");
      if (typeof leaf.value === "number") breakpoints.add(leaf.value);
    });
  });

  return { property_used, breakpoints: Array.from(breakpoints).sort((a, b) => a - b), breakpoint_method: "manual" };
}
